import { API } from "aws-amplify";
import { Decision } from "@/api/model/Decision";
import { Viewpoint } from "@/api/model/Viewpoint";
import { ModelDefinition } from "@/api/model/ModelDefinition";
import { FactorResource } from "@/api/model/FactorResource";
import { Choice } from "@/api/model/Choice";
import { ViewpointFactorResource } from "@/api/model/ViewpointFactorResource";
import { ViewpointChoiceFactor } from "@/api/model/ViewpointChoiceFactor";
import { DecisionUser } from "@/api/model/DecisionUser";
import { ViewpointChoiceScore } from "@/api/model/ViewpointChoiceScore";

const API_NAME = "Viewpoint360API";

export class ViewpointApi {

    public async getDecisions(): Promise<Decision[]> {
        return await API.get(API_NAME, `/decisions`, {});
    }

    public async createDecision(name: string, description: string, guest?: boolean): Promise<Decision> {
        let params = {
            body: {
                name: name,
                description: description,
                fromTemplateId: 'SANO',
                choicesSourceSystem: 'MLS',
                ttl: guest ? (new Date().getTime() / 1000) + (60 * 60 * 24 * 30) : null

            }
        }
        return await API.post(API_NAME, '/decisions', params);
    }

    public async getDecision(decisionId: string): Promise<Decision> {
        return await API.get(API_NAME, `/decisions/${decisionId}`, {});
    }

    public async updateDecision(decisionId: string, name?: string, description?: string, group?: string): Promise<Decision> {
        let params = {
            body: {
                name: name,
                description: description,
                group: group
            }
        }
        return await API.post(API_NAME, `/decisions/${decisionId}`, params);
    }

    public async deleteDecision(decisionId: string): Promise<Decision> {
        return await API.del(API_NAME, `/decisions/${decisionId}`, {});
    }

    public async getDecisionUsers(decisionId: string): Promise<DecisionUser[]> {
        return await API.get(API_NAME, `/decisions/${decisionId}/users`, {});
    }

    async removeDecisionUser(decisionId: string, userId: string) {
        return await API.del(API_NAME, `/decisions/${decisionId}/users/${userId}`, {})
    }

    public async getDecisionViewpoints(decisionId: string): Promise<Viewpoint[]> {
        let params = {
            queryStringParameters: {
                decisionId: decisionId
            }
        }
        return await API.get(API_NAME, `/viewpoints`, params);
    }

    public async createViewpoint(decisionId: string, name: string, description: string, scoringKeyDefinition?: unknown): Promise<Viewpoint> {
        let params = {
            body: {
                decisionId: decisionId,
                name: name,
                description: description,
                scoringKeyDefinition: scoringKeyDefinition
            }
        }

        return await API.post(API_NAME, `/viewpoints`, params);
    }

    public async deleteViewpoint(viewpointId: string): Promise<Viewpoint> {
        return await API.del(API_NAME, `/viewpoints/${viewpointId}`, {})
    }

    public async updateViewpoint(payload: Viewpoint) {
        let params = {
            body: {
                name: payload.name,
                description: payload.description,
                visibility: payload.visibility,
                visibleUsers: payload.visibleUsers
            }
        }
        return await API.post(API_NAME, `/viewpoints/${payload.viewpointId}`, params);
    }

    public async getDecisionModel(modelId: string, revision?: string): Promise<ModelDefinition> {
        if (revision) {
            return await API.get(API_NAME, `/model/${modelId}/${revision}`, {})
        } else {
            return await API.get(API_NAME, `/model/${modelId}`, {})
        }
    }

    public async getModelFactorResources(modelId: string, revision: string | number): Promise<FactorResource[]> {
        return await API.get(API_NAME, `/model/${modelId}/${revision}/resources`, {});
    }


    public async getViewpointFactorResources(viewpointId: string): Promise<ViewpointFactorResource[]> {
        return await API.get(API_NAME, `/viewpoints/${viewpointId}/factors`, {});
    }

    public async putViewpointFactorResource(viewpointId: string, viewpointFactorResource: ViewpointFactorResource) {
        let params = {
            body: viewpointFactorResource
        }

        return await API.post(API_NAME, `/viewpoints/${viewpointId}/factors/${viewpointFactorResource.id}`, params);
    }


    public async putViewpointFactorResources(viewpointId: string, viewpointFactorResources: ViewpointFactorResource[]): Promise<ViewpointFactorResource[]> {
        let params = {
            body: viewpointFactorResources
        }

        return await API.post(API_NAME, `/viewpoints/${viewpointId}/factors`, params);
    }

    public async getViewpointChoiceFactors(viewpointId: string): Promise<ViewpointChoiceFactor[]> {
        return await API.get(API_NAME, `/viewpoints/${viewpointId}/choice/factors`, {});
    }

    public async putViewpointChoiceFactors(viewpointId: string, viewpointChoiceFactors: ViewpointChoiceFactor[]): Promise<ViewpointChoiceFactor[]> {
        let params = {
            body: viewpointChoiceFactors
        }
        return await API.post(API_NAME, `/viewpoints/${viewpointId}/choice/factors`, params);
    }

    public async getScores(decisionId: string, revision: string | number, viewpointIds: string[]):
        Promise<{ viewpointScores: { [viewpointId: string]: ViewpointChoiceScore } }> {
        let params = {
            queryStringParameters: {
                viewpointId: viewpointIds
            },
        }
        return await API.get(API_NAME, `/scoring/${decisionId}/${revision}`, params);
    }

    public async getViewpointScore(decisionId: string, revision: string | number, viewpointId: string, choiceIds?: string[]): Promise<ViewpointChoiceScore> {
        let params = {}
        if (choiceIds && choiceIds.length) {
            params = {
                queryStringParameters: {
                    choiceId: choiceIds
                }
            }
        }
        return await API.get(API_NAME, `/scoring/${decisionId}/${revision}/viewpoint/${viewpointId}`, params);
    }

    public async getChoices(choicesSourceId: string, choiceIds?: string[]): Promise<Choice[]> {
        let params = {}
        if (choiceIds) {
            params = {
                queryStringParameters: {
                    choiceIds: choiceIds
                }
            }
        }
        return (await API.get(API_NAME, `/choices/${choicesSourceId}`, params));
    }

    public async getConstraintChoices(choicesSourceId: string, constraints: any): Promise<Choice[]> {
        let params = {
            body: constraints
        }
        return (await API.post(API_NAME, `/choices/${choicesSourceId}/search`, params)).choices;
    }


    public async createChoice(choicesSourceId: string, choice: Choice) {
        let params = {
            body: [choice]
        }
        return (await API.post(API_NAME, `/choices/${choicesSourceId}`, params))[0]
    }

    public async createChoices(choicesSourceId: string, choices: Choice[]) {
        let params = {
            body: choices
        }
        return await API.post(API_NAME, `/choices/${choicesSourceId}`, params)
    }

    public async updateChoice(choicesSourceId: string, choiceId: string, choice: Choice) {
        let params = {
            body: choice
        }
        return await API.post(API_NAME, `/choices/${choicesSourceId}/${choiceId}`, params)
    }

    public async deleteChoice(choicesSourceId: string, choiceId: string) {
        return await API.del(API_NAME, `/choices/${choicesSourceId}/${choiceId}`, {})
    }

    async inviteDecisionUserByEmail(decisionId: string, email: string) {
        let params = {
            body: {
                email: email,
                permissionAccess: "EDIT"
            }
        }
        return await API.post(API_NAME, `/decisions/${decisionId}/users`, params)
    }

    async inviteDecisionUserByUserId(decisionId: string, userId: string) {
        let params = {
            body: {
                userId: userId,
                permissionAccess: "EDIT"
            }
        }
        return await API.post(API_NAME, `/decisions/${decisionId}/users`, params)
    }

    async joinDecision(decisionId: string, passcode: string, email: string) {
        let params = {
            body: {
                decisionId: decisionId,
                passcode: passcode,
                email: email
            }
        }
        return await API.post(API_NAME, `/decisions/${decisionId}/users`, params)
    }

    async modifyDecisionUserAccess(decisionId: string, userId: string, permissionAccess: string) {
        let params = {
            body: {
                permissionAccess: permissionAccess,
            }
        }
        return await API.post(API_NAME, `/decisions/${decisionId}/users/${userId}`, params)
    }

    async getFavouriteList(decisionId: string, userId: string) {
        let id = 'FAVOURITE_' + userId
        let favList = await API.get(API_NAME, `/lists/${decisionId}/${id}`, {});
        if (favList) return favList.choices.map((c: any) => c.choiceId)
        return [];
    }

    async updateFavouriteList(decisionId: string, userId: string, choices: string[]) {
        let id = 'FAVOURITE_' + userId
        let choiceUploadData = choices.map(id => {
            return {
                choiceId: id
            }
        })
        let params = {
            body: {
                name: "Favourite Properties",
                decisionId: decisionId,
                listId: id,
                type: "CHOICE",
                ownerUserId: userId,
                choices: choiceUploadData
            }
        }
        let updatedData: any = await API.post(API_NAME, `/lists/${decisionId}/${id}`, params);
        return updatedData.choices.map((c: any) => c.choiceId)
    }

}

export const VIEWPOINT_API = new ViewpointApi();